import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramDescriptions = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [descriptionsData, setDescriptionsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // const { selectedResult } = useAppContext(); // Removed context usage

  // Fetch descriptions data (wrapped in useCallback)
  const fetchDescriptions = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getDescriptions(programId); // Use programId prop
      setDescriptionsData(data || []); // Ensure data is an array
    } catch (err) {
      console.error('Failed to fetch descriptions data:', err);
      setError(t('programDescriptions.error'));
      setDescriptionsData([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchDescriptions();
  }, [fetchDescriptions]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('programDescriptions.loading')}</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{t('programDescriptions.error')}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={fetchDescriptions} // Retry fetch
          >
            {t('programDescriptions.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!descriptionsData || descriptionsData.length === 0) { // Check if descriptionsData is null or empty
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('programDescriptions.noDescriptions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto">
      <div className="min-w-full">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left sticky top-0">
            <tr>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programDescriptions.columns.type')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programDescriptions.columns.language')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programDescriptions.columns.size')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programDescriptions.columns.description')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programDescriptions.columns.tmsId')}</th>
            </tr>
          </thead>
          <tbody>
            {descriptionsData.map((item, index) => (
              <tr key={index} className="border-t border-gray-200">
                <td className="px-4 py-3 text-left">{item.type || '-'}</td>
                <td className="px-4 py-3 text-left">{item.lang || '-'}</td>
                <td className="px-4 py-3 text-left">{item.size || '-'}</td>
                <td className="px-4 py-3 text-left">
                  <div className="max-w-lg break-words">
                    {item.description || '-'}
                  </div>
                </td>
                <td className="px-4 py-3 text-left">{item.TMSId || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-between text-xs text-gray-500 px-4 pb-4">
          <div>
            {t('programDescriptions.showing', { count: descriptionsData.length })}
          </div>
          <div>
            <button
              className={`px-3 py-1 border border-gray-300 rounded-sm ${descriptionsData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              onClick={() => exportToCsv(descriptionsData, `${programId}_descriptions.csv`)}
              disabled={descriptionsData.length === 0}
            >
              {t('programDescriptions.export')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDescriptions;
